// Cloudflare Turnstile helper for fetch-driven forms.
//
// Classic form posts don't need this — a `.cf-turnstile` div with a
// `data-sitekey` auto-renders and injects its own hidden input. This is for the
// places we submit with fetch and need a token in hand first.

const TIMEOUT = 15000

/**
 * Builds a token getter bound to one widget.
 *
 * Returns an async function that renders the widget on first use, resets it on
 * subsequent uses (tokens are single-use), and resolves to a token — or null if
 * Turnstile isn't configured, hasn't loaded, or fails. The server decides how to
 * treat a null token.
 *
 * @param {string} siteKey
 * @returns {(container: HTMLElement) => Promise<string|null>}
 */
export const createTurnstile = (siteKey) => {
	let widgetId = null
	let resolveToken = null

	const settle = (value) => {
		const resolve = resolveToken
		resolveToken = null

		if (resolve) {
			resolve(value || null)
		}
	}

	return async (container) => {
		if (!siteKey || !container || typeof window.turnstile === 'undefined') {
			return null
		}

		const token = new Promise((resolve) => {
			resolveToken = resolve

			try {
				if (widgetId === null) {
					widgetId = window.turnstile.render(container, {
						'sitekey': siteKey,
						'execution': 'execute',
						'appearance': 'interaction-only',
						'callback': settle,
						'error-callback': () => settle(null),
						'timeout-callback': () => settle(null),
						'expired-callback': () => settle(null),
					})
				} else {
					window.turnstile.reset(widgetId)
				}

				window.turnstile.execute(widgetId)
			} catch (error) {
				console.error('Turnstile failed to execute:', error)
				settle(null)
			}
		})

		// Don't let a silent failure leave a button spinning forever.
		const timeout = new Promise((resolve) =>
			window.setTimeout(() => resolve(null), TIMEOUT),
		)

		return Promise.race([token, timeout])
	}
}
